import React, { useCallback } from "react";
import { connect } from "react-redux";

import clsx from "clsx";
import { LocalDate, LocalTime } from "js-joda";
import { bindActionCreators, Dispatch } from "redux";

import { DateInput, TimeInput, NumericInput } from "@plex/react-components";

import { InlineEdit } from "ui/components";

import { ITokenItem, IChangeToken, TokenFormat } from "../../shared/types/Token.types";
import { translationMessagesActions } from "../TranslationMessages.actions";

import styles from "./TranslationMessagesGrid.module.scss";

export interface ITranslationMessagesTokenValueItem {
  token: ITokenItem;
  rowIndex: number;
  tokenIndex: number;
  changeTokenValue: (change: IChangeToken) => void;
}

export const TranslationMessagesTokenValueItem: React.FC<ITranslationMessagesTokenValueItem> = ({ token, rowIndex, tokenIndex, changeTokenValue }) => {
  const isEditableToken = useCallback(() => {
    return true;
  }, []);

  const onDateValueChange = useCallback(
    (name: string, value: LocalDate | null, rowIndex: number, tokenIndex: number) => {
      changeTokenValue({
        name: name,
        value: value || LocalDate.now(),
        displayValue: value,
        rowIndex: rowIndex,
        tokenIndex: tokenIndex
      });
    },
    [changeTokenValue]
  );

  const onTimeValueChange = useCallback(
    (name: string, value: LocalTime | null, rowIndex: number, tokenIndex: number) => {
      changeTokenValue({
        name: name,
        value: value || LocalTime.now(),
        displayValue: value && value.toString(),
        rowIndex: rowIndex,
        tokenIndex: tokenIndex
      });
    },
    [changeTokenValue]
  );

  const onTokenValueChange = useCallback(
    (name: string, value: number | null, rowIndex: number, tokenIndex: number) => {
      changeTokenValue({
        name: name,
        value: value,
        displayValue: value,
        rowIndex: rowIndex,
        tokenIndex: tokenIndex
      });
    },
    [changeTokenValue]
  );

  const renderTokenPreviewField = (token: ITokenItem, rowIndex: number, tokenIndex: number) => {
    switch (token.type) {
      case TokenFormat.Date:
        return (
          <DateInput
            className={styles.dateInput}
            name={token.name}
            value={token.displayValue}
            onValueChange={value => onDateValueChange(token.name, value, rowIndex, tokenIndex)}
          />
        );
      case TokenFormat.Time:
        return <TimeInput value={token.displayValue} onValueChange={value => onTimeValueChange(token.name, value, rowIndex, tokenIndex)} formatOptions={{ time: "short" }} />;
      case TokenFormat.Number:
        return <NumericInput className={styles.numericInput} numericValue={token.value} onValueChange={value => onTokenValueChange(token.name, value, rowIndex, tokenIndex)} />;
      case TokenFormat.Plural:
        return <NumericInput className={styles.numericInput} numericValue={token.value} onValueChange={value => onTokenValueChange(token.name, value, rowIndex, tokenIndex)} />;
      case TokenFormat.SelectOrdinal:
        return <NumericInput className={styles.numericInput} numericValue={token.value} onValueChange={value => onTokenValueChange(token.name, value, rowIndex, tokenIndex)} />;
      default:
        return (
          <InlineEdit<ITokenItem>
            initialValue={token.value}
            onValueChanged={value => changeTokenValue({ name: token.name, value: value, rowIndex: rowIndex, tokenIndex: tokenIndex })}
            className={styles.inlineEditToken}
            canEdit={isEditableToken}
            context={token}
          >
            <p className={styles.wrapText}>{token.value}</p>
          </InlineEdit>
        );
    }
  };

  const isAddPadding =
    token.type === TokenFormat.Date ||
    token.type === TokenFormat.Time ||
    token.type === TokenFormat.Number ||
    token.type === TokenFormat.Plural ||
    token.type === TokenFormat.SelectOrdinal;

  return (
    <div className={styles.tokenRow}>
      <div key={tokenIndex} className={styles.tokenName}>
        {token.name}
      </div>
      <div className={clsx(styles.tokenValue, isAddPadding && styles.tokenValuePadding)}>{renderTokenPreviewField(token, rowIndex, tokenIndex)}</div>
    </div>
  );
};

/* istanbul ignore next */
export default connect((dispatch: Dispatch) => {
  return bindActionCreators(
    {
      changeTokenValue: translationMessagesActions.changeTokenValue
    },
    dispatch
  );
})(TranslationMessagesTokenValueItem);
